/** @jsx isolateComponent */
import { Fragment } from 'react';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { css } from '@emotion/react';

import {
  BrandButton,
  Button,
  ButtonProps,
  Colors,
  DarkBackgroundButton,
  Heading,
  SubscriptionFormWithDisclaimer,
  Text,
  Typography,
  Icon,
  TagLabel,
  Grid,
} from '../../../../index';
import { CTAColor, HeroColorsV5 } from '../types';
import {
  mapBackgroundColorToV5HeadingColorName,
  mapBackgroundColorToV5TextColorName,
  validateZapierBackgroundAccessibility,
  getSupportedCTAColor,
} from '../utils';
import type { AnchorTarget } from '../../../../ts-types';
import type { IconName } from '../../../display/Icon';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'neutral100' | 'zapier' | 'night' | 'moss' | 'neutral800'`.
   * */
  backgroundColor?: HeroColorsV5;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Background color of the CTA button.
   */
  ctaColor?: CTAColor;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Text displayed next to CTA button.
   */
  ctaDisclaimer?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component. */
  children: React.ReactNode;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'info'
    | 'success'
    | 'error'
    | 'warning'
    | 'white'
    | 'gray'
    | 'peach'
    | 'neutral300'
    | 'bolt'
    | 'cream';
};

const breakpoints = {
  tablet: 768,
  desktop: 1230,
};

const SubscriptionFormStyles = {
  ctaContainer: css`
    max-width: 350px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 470px;
      align-items: stretch;
      flex-direction: column;
    }
  `,
  ctaDisclaimer: css`
    margin: 10px 0;
    text-align: left;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 10px 0;
    }
  `,
};

const Styles = labelStyles('HeroFeatures', {
  root: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${Colors[backgroundColor]};
  `,
  mediaContent: css`
    width: 100%;
    img {
      min-height: 341px;
      object-fit: cover;
    }
  `,
  content: css`
    margin: 0 auto;
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 80px 0;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 85px 0;
    }
    max-width: 770px;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  textContent: css`
    margin: auto;
  `,
  labelContainer: css`
    margin-bottom: 10px;
  `,
  heading: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    ${Typography.pageHeader4}
    color: ${Colors[mapBackgroundColorToV5TextColorName(backgroundColor)]};
    margin-bottom: 10px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.pageHeader3}
    }
  `,
  description: (
    backgroundColor: Props['backgroundColor'] = 'neutral100'
  ) => css`
    ${Typography.pageHeader9}
    color: ${Colors[mapBackgroundColorToV5HeadingColorName(backgroundColor)]};
    text-align: center;
    margin: 0 auto;
    max-width: 570px;
  `,
  ctaContainer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin-top: 30px;
    text-align: center;
    width: 100%;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
    }
    ${isSubscriptionForm &&
    withModifier('subscription-form', SubscriptionFormStyles.ctaContainer)}
  `,
  ctaButtonWrapper: css`
    margin: 0 auto;

    // Button should take up full width in mobile only;
    button,
    a {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      button,
      a {
        width: auto;
      }
    }
  `,
  ctaDisclaimer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin: 10px 0 0 0;
    text-align: center;
    ${isSubscriptionForm &&
    withModifier(
      'subscription-form-disclaimer',
      SubscriptionFormStyles.ctaDisclaimer
    )}
  `,
});

export const HeroFeatures = ({
  backgroundColor = 'neutral100',
  ctaColor = 'primary',
  isSubscriptionForm = false,
  subscriptionPlaceholder = 'Email address',
  heading,
  description,
  ctaText,
  ctaHref,
  ctaDisclaimer,
  onCtaClick,
  onCtaKeyDown,
  children,
  onSubscriptionFormSubmit,
  ctaTarget,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  // This should validate and through a console error as the disclaimer text accessibility
  // is poor on the zapier background.
  validateZapierBackgroundAccessibility(
    backgroundColor,
    isSubscriptionForm,
    !!ctaDisclaimer
  );

  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: !isSubscriptionForm ? ctaHref : undefined,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      type: isSubscriptionForm
        ? ('submit' as ButtonProps['type'])
        : ('button' as ButtonProps['type']),
      'data-testid': 'standard-hero-cta-button',
      target: ctaTarget,
    };

    const color = getSupportedCTAColor(ctaColor, backgroundColor);

    return color === 'brand' ? (
      <BrandButton {...sharedProps} />
    ) : color === 'primary-dark' ? (
      <DarkBackgroundButton {...sharedProps} />
    ) : (
      <Button
        {...sharedProps}
        color={color}
        {...(isSubscriptionForm && { size: 'small' })}
      />
    );
  };

  return (
    <div
      css={Styles.root(backgroundColor)}
      data-background-color={backgroundColor}
    >
      <div css={Styles.mediaContent}>{children}</div>
      <div css={Styles.content}>
        {label && (
          <div css={Styles.labelContainer}>
            <TagLabel color={labelColor}>
              {labelIcon ? (
                <Grid alignItems="center" columns="auto max-content" gap={5}>
                  <Icon name={labelIcon} size={20} />
                  {label}
                </Grid>
              ) : (
                label
              )}
            </TagLabel>
          </div>
        )}
        <Heading css={Styles.heading(backgroundColor)}>{heading}</Heading>
        {description && (
          <div css={Styles.description(backgroundColor)}>{description}</div>
        )}
        {ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (
          <div css={Styles.ctaContainer(isSubscriptionForm)}>
            {isSubscriptionForm ? (
              <SubscriptionFormWithDisclaimer
                placeholderText={subscriptionPlaceholder}
                onSubmit={onSubscriptionFormSubmit}
                renderButton={() => getCtaComponent()}
                disclaimerTextColor={
                  backgroundColor === 'neutral100' ? 'primary' : 'secondary'
                }
              />
            ) : (
              <Fragment>
                <div css={Styles.ctaButtonWrapper}>{getCtaComponent()}</div>
                {ctaDisclaimer && (
                  <div css={Styles.ctaDisclaimer(isSubscriptionForm)}>
                    <Text
                      tag="div"
                      type="paragraph3"
                      color={mapBackgroundColorToV5HeadingColorName(
                        backgroundColor
                      )}
                    >
                      {ctaDisclaimer}
                    </Text>
                  </div>
                )}
              </Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
